if (!customElements.get('cart-drawer')) {
  const CART_SECTION = 'cart-drawer';

  const PAGE_FOCUS_KEY = 'parvum:cart-page-focus';

  const STOREFRONT_VERSION = '2026-10';

  const DELIVERY_FIELDS = `
    delivery { addresses { id selected address { ... on CartDeliveryAddress { address1 zip countryCode } } } }
    deliveryGroups(first: 10) { nodes { deliveryOptions { estimatedCost { amount } } } }
  `;

  const DELIVERY_QUERY = `query ($cartId: ID!) { cart(id: $cartId) { ${DELIVERY_FIELDS} } }`;

  const DELIVERY_ADD = `mutation ($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
    cartDeliveryAddressesAdd(cartId: $cartId, addresses: $addresses) { cart { ${DELIVERY_FIELDS} } }
  }`;

  const DELIVERY_REMOVE = `mutation ($cartId: ID!, $addressIds: [ID!]!) {
    cartDeliveryAddressesRemove(cartId: $cartId, addressIds: $addressIds) { cart { id } }
  }`;

  class CartDrawer extends HTMLElement {
    #busy = false;
    #deliveryRequest = 0;
    #deliveryRun = Promise.resolve();
    #deliveryPending = 0;
    #delivery = null;

    connectedCallback() {
      this.dialog = this.querySelector('[data-cart-drawer]');
      if (!this.dialog) return;

      this.onPage = Boolean(document.querySelector('[data-cart-page]'));
      this.status = this.dialog.querySelector('[data-cart-status]');
      if (this.onPage) this.#restorePageFocus();

      this.#applyDelivery();
      this.#applyFreeBar();

      document.addEventListener('click', this.#onDocumentClick);
      document.addEventListener('submit', this.#onSubmit);
      this.dialog.addEventListener('click', this.#onDialogClick);
      this.dialog.addEventListener('change', this.#onDialogChange);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.#onDocumentClick);
      document.removeEventListener('submit', this.#onSubmit);
      this.dialog?.removeEventListener('click', this.#onDialogClick);
      this.dialog?.removeEventListener('change', this.#onDialogChange);
    }

    get #root() {
      return window.Shopify?.routes?.root || '/';
    }

    get #body() {
      return this.dialog.querySelector('[data-cart-drawer-body]');
    }

    open() {
      for (const other of document.querySelectorAll('dialog[open]')) {
        if (other !== this.dialog) other.close();
      }
      if (!this.dialog.open) this.dialog.showModal();
      this.#body?.focus({ preventScroll: true });
    }

    close() {
      this.dialog.close();
    }

    #onDocumentClick = (event) => {
      const step = this.onPage && event.target.closest('[data-cart-page] [data-cart-change]');
      if (step) this.#rememberPageFocus(step);

      const link = event.target.closest('[data-cart-open]');
      if (!link || this.onPage) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      this.open();
    };

    #onDialogClick = (event) => {
      if (event.target === this.dialog || event.target.closest('[data-cart-close]')) {
        this.close();
        return;
      }

      const change = event.target.closest('[data-cart-change]');
      if (!change) return;
      event.preventDefault();
      this.#change(change);
    };

    #onDialogChange = (event) => {
      const colour = event.target.closest('[data-complement-colour]');
      if (!colour) return;
      const row = colour.closest('[data-complement]');
      if (!row) return;

      const id = row.querySelector('[data-complement-id]');
      if (id) id.value = colour.value;

      const price = row.querySelector('[data-complement-price]');
      if (price) price.textContent = colour.dataset.price;

      const was = row.querySelector('[data-complement-was]');
      if (was) {
        was.textContent = colour.dataset.was;
        was.closest('s').hidden = !colour.dataset.was;
      }

      for (const thumb of row.querySelectorAll('[data-complement-thumb]')) {
        thumb.hidden = thumb.dataset.complementThumb !== colour.value;
      }
    };

    #onSubmit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      if (event.submitter?.name === 'checkout' && this.#deliveryPending && !('deliveryChecked' in form.dataset)) {
        event.preventDefault();
        const submitter = event.submitter;
        submitter.setAttribute('aria-busy', 'true');
        const timeout = new Promise((resolve) => setTimeout(resolve, 3000));
        Promise.race([this.#deliveryRun, timeout]).then(() => {
          form.dataset.deliveryChecked = '';
          setTimeout(() => form.requestSubmit(submitter));
        });
        return;
      }

      if (form.matches('[data-cart-discount]') && this.dialog.contains(form)) {
        event.preventDefault();
        this.#discount(form);
        return;
      }

      const complement = form.matches('[data-cart-add]') && this.dialog.contains(form);
      if (!complement && !form.matches('.buy__form')) return;
      event.preventDefault();
      this.#add(form, event.submitter);
    };

    async #discount(form) {
      if (this.#busy) return;
      this.#busy = true;

      const entered = form.dataset.codes === undefined ? '' : form.elements.discount.value.trim();
      let discount = form.elements.discount.value.trim();
      if (form.dataset.codes !== undefined) {
        const codes = form.dataset.codes.split(',').filter(Boolean);
        if (entered && !codes.some((code) => code.toLowerCase() === entered.toLowerCase())) {
          codes.push(entered);
        }
        discount = codes.join(',');
      }

      try {
        let data = await this.#updateDiscount(discount);
        const refused = (data.discount_codes || []).filter((code) => !code.applicable);
        const kept = (data.discount_codes || []).filter((code) => code.applicable).map((code) => code.code);
        const same = (code) => code.code.toLowerCase() === entered.toLowerCase();
        let worksAlone = false;

        if (entered && refused.some(same) && kept.length) {
          const alone = await this.#updateDiscount(entered);
          worksAlone = (alone.discount_codes || []).some((code) => same(code) && code.applicable);
        }

        if (refused.length || worksAlone) {
          data = await this.#updateDiscount(kept.join(','));
        }

        this.#render(data.sections?.[CART_SECTION]);
        const field = this.#body.querySelector('[data-cart-discount] input[name="discount"]:not([type="hidden"])');
        (field || this.#body).focus({ preventScroll: true });

        const applied = (data.discount_codes || []).some((code) => code.applicable && entered && code.code.toLowerCase() === entered.toLowerCase());
        if (applied && !refused.length) {
          this.#say('codeApplied', { CODE: entered });
        } else if (form.dataset.codes === undefined) {
          this.#say('codeRemoved');
        }

        if (entered && worksAlone) {
          this.#showMessage(form.dataset.kept, { CODE: entered, OLD: kept.join(', ') }, 'notice');
        } else if (entered && refused.some(same)) {
          this.#showMessage(form.dataset.notApplicable, { CODE: entered });
        } else if (entered && refused.length) {
          const old = refused.map((code) => code.code).join(', ');
          this.#showMessage(form.dataset.replaced, { CODE: entered, OLD: old }, 'notice');
        }
      } catch {
        form.submit();
      } finally {
        this.#busy = false;
      }
    }

    async #add(form, submitter) {
      if (this.#busy) return;
      this.#busy = true;
      submitter?.setAttribute('aria-busy', 'true');

      const body = new FormData(form);
      body.append('sections', CART_SECTION);
      body.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(`${this.#root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
        const data = await response.json();

        if (response.ok) {
          this.#render(data.sections?.[CART_SECTION]);
        } else {
          await this.#reload();
          this.#showError(data.description || data.message);
        }
        this.open();
        if (response.ok) this.#say('added');
      } catch {
        form.submit();
      } finally {
        this.#busy = false;
        submitter?.removeAttribute('aria-busy');
      }
    }

    async #change(link) {
      if (this.#busy) return;
      this.#busy = true;

      const key = link.dataset.key;
      const quantity = Number(link.dataset.quantity);
      const index = Array.from(this.#body.querySelectorAll(`[data-key="${CSS.escape(key)}"]`)).indexOf(link);
      const line = link.closest('.cart-line');
      const name = [line?.querySelector('.cart-line__name'), line?.querySelector('.cart-line__details')]
        .map((part) => part?.textContent.trim())
        .filter(Boolean)
        .join(', ');
      this.#body.querySelector('.cart-lines')?.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${this.#root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: CART_SECTION,
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();

        if (response.ok) {
          this.#render(data.sections?.[CART_SECTION]);
          this.#restoreFocus(key, index);
          if (quantity === 0) {
            this.#say('removed', { NAME: name });
          } else {
            this.#say('quantity', { NAME: name, QUANTITY: String(quantity) });
          }
        } else {
          await this.#reload();
          this.#showError(data.description || data.message);
        }
      } catch {
        window.location.href = link.href;
      } finally {
        this.#busy = false;
      }
    }

    async #updateDiscount(discount) {
      const response = await fetch(`${this.#root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount,
          sections: CART_SECTION,
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error(String(response.status));
      return response.json();
    }

    async #reload() {
      try {
        const response = await fetch(`${window.location.pathname}?sections=${CART_SECTION}`);
        const data = await response.json();
        this.#render(data[CART_SECTION]);
      } catch {}
    }

    #render(html) {
      if (!html) return;
      const next = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-cart-drawer-body]');
      if (!next) return;

      this.#body.innerHTML = next.innerHTML;
      if (this.#delivery) this.#showDelivery(this.#delivery);
      this.#applyDelivery();
      this.#applyFreeBar();
      this.#updateIcons();
    }

    #applyDelivery() {
      const request = ++this.#deliveryRequest;
      this.#deliveryPending += 1;
      this.#deliveryRun = this.#deliveryRun
        .then(() => this.#loadDelivery(request))
        .finally(() => {
          this.#deliveryPending -= 1;
        });
    }

    async #loadDelivery(request) {
      const row = document.querySelector('[data-delivery]');
      if (!row?.dataset.zip || request !== this.#deliveryRequest) return;

      const cartTotal = document.querySelector('[data-free-delivery]')?.dataset.total;

      try {
        const cartId = await this.#cartId();
        if (!cartId) return;

        const { cart } = await this.#storefront(DELIVERY_QUERY, { cartId });
        const own = cart?.delivery?.addresses?.find((entry) => entry.selected && entry.address?.address1);
        if (own && own.address.countryCode !== row.dataset.country) return;

        let result = cart;
        if (!own) {
          const address = { countryCode: row.dataset.country, zip: row.dataset.zip };
          if (row.dataset.province) address.provinceCode = row.dataset.province;
          const added = await this.#storefront(DELIVERY_ADD, {
            cartId,
            addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress: address } }],
          });
          result = added.cartDeliveryAddressesAdd?.cart;
        }

        const samples = (result?.delivery?.addresses || []).filter((entry) => !entry.address?.address1);
        if (samples.length) {
          await this.#storefront(DELIVERY_REMOVE, { cartId, addressIds: samples.map((entry) => entry.id) });
        }

        const groups = result?.deliveryGroups?.nodes || [];
        if (request !== this.#deliveryRequest) return;
        const cheapest = groups.map((group) =>
          Math.min(...group.deliveryOptions.map((option) => Math.round(Number(option.estimatedCost.amount) * 100)))
        );
        if (!cheapest.length || cheapest.some((cents) => !Number.isFinite(cents))) return;

        this.#delivery = {
          country: row.dataset.country,
          cents: cheapest.reduce((sum, cents) => sum + cents, 0),
          standard: this.#convertPrice(Number(row.dataset.rate) || 0),
          cartTotal,
          exact: Boolean(own),
        };
        this.#showDelivery(this.#delivery);
        this.#applyFreeBar();
      } catch {}
    }

    async #cartId() {
      const cookie = document.cookie.split('; ').find((part) => part.startsWith('cart='));
      let token = cookie ? decodeURIComponent(cookie.slice(5)) : '';
      if (!token.includes('?key=')) {
        const response = await fetch(`${this.#root}cart.js`);
        token = response.ok ? (await response.json()).token : '';
      }
      return token ? `gid://shopify/Cart/${token}` : '';
    }

    async #storefront(query, variables) {
      const response = await fetch(`/api/${STOREFRONT_VERSION}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query, variables }),
      });
      if (!response.ok) throw new Error(String(response.status));
      const { data, errors } = await response.json();
      if (errors?.length || !data) throw new Error(errors?.[0]?.message || 'storefront');
      return data;
    }

    #showDelivery({ country, cents, standard, exact }) {
      const rows = [...document.querySelectorAll('[data-delivery]')].filter(
        (element) => element.dataset.country === country
      );
      if (!rows.length) return;

      for (const element of rows) {
        const sample = element.dataset.moneySample;
        const price = element.querySelector('[data-delivery-price]');
        if (price) {
          if (cents === 0 && standard > 0) {
            const was = document.createElement('s');
            was.textContent = this.#formatMoney(standard, sample);
            price.replaceChildren(was, element.dataset.free);
          } else if (cents === 0) {
            price.textContent = element.dataset.free;
          } else {
            price.textContent = this.#formatMoney(cents, sample);
          }
          if (element.hasAttribute('data-remote') && !exact) price.append('*');
        }
        element.hidden = false;

        const total = element.closest('.cart-summary')?.querySelector('[data-cart-total]');
        if (total) total.textContent = this.#formatMoney(Number(total.dataset.cartTotal) + cents, sample);
      }
      for (const note of document.querySelectorAll('[data-delivery-note]')) note.hidden = true;
      for (const note of document.querySelectorAll('[data-delivery-remote]')) note.hidden = Boolean(exact);
    }

    #convertPrice(cents) {
      const rate = Number(window.Shopify?.currency?.rate) || 1;
      if (rate === 1) return cents;
      return Math.ceil((cents * rate) / 100) * 100;
    }

    #applyFreeBar() {
      const rate = Number(window.Shopify?.currency?.rate);
      if (!rate) return;

      for (const bar of document.querySelectorAll('[data-free-delivery]')) {
        const threshold = Math.round(Number(bar.dataset.freeFrom) * rate);
        const total = Number(bar.dataset.total);
        const remaining = threshold - total;
        const freeRate = this.#delivery?.cents === 0 && this.#delivery.cartTotal === bar.dataset.total;
        const reached = remaining <= 0 || freeRate;

        bar.classList.toggle('free-delivery--reached', reached);
        const text = bar.querySelector('[data-free-remaining]');
        if (text && !reached) {
          const amount = this.#formatMoney(remaining, bar.dataset.moneySample).replace(/\u00a0/g, ' ');
          text.textContent = bar.dataset.remainingText.replace('%AMOUNT%', amount);
        }
        const fill = bar.querySelector('[data-free-fill]');
        if (fill) fill.style.width = `${reached ? 100 : Math.floor((total * 100) / threshold)}%`;
        bar.hidden = false;
      }
    }

    #formatMoney(cents, sample) {
      const parts = sample.match(/^(.*?)1(\D?)234(\D)56(.*)$/s);
      const whole = Math.floor(cents / 100).toString();
      const fraction = String(cents % 100).padStart(2, '0');
      if (!parts) return `${whole}.${fraction}`;

      const [, before, thousands, decimal, after] = parts;
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return `${before}${grouped}${decimal}${fraction}${after}`;
    }

    #updateIcons() {
      const template = this.#body.querySelector('template[data-cart-icon]');
      if (!template) return;
      const icon = template.content.querySelector('svg');

      for (const link of document.querySelectorAll('[data-cart-open]')) {
        link.setAttribute('aria-label', template.dataset.label);
        const count = link.querySelector('[data-cart-count]');
        if (count) count.textContent = template.dataset.count;
        if (icon) link.querySelector('svg')?.replaceWith(icon.cloneNode(true));
      }
    }

    #showError(message) {
      this.#showMessage(message);
    }

    #showMessage(template, values = {}, tone = 'error') {
      const box = this.#body.querySelector('[data-cart-error]');
      if (!box || !template) return;

      const parts = template.split(/%(CODE|OLD)%/);
      box.replaceChildren(
        ...parts.map((part, index) => {
          if (index % 2 === 0) return part;
          const bold = document.createElement('b');
          bold.textContent = values[part] ?? '';
          return bold;
        })
      );
      box.dataset.tone = tone;
      box.removeAttribute('role');
      box.hidden = false;
      this.#announce(box.textContent);
    }

    #say(key, values = {}) {
      const template = this.status?.dataset[key];
      if (!template) return;
      this.#announce(template.replace(/%(NAME|QUANTITY|CODE)%/g, (match, part) => values[part] ?? ''));
    }

    #announce(message) {
      if (!this.status || !message) return;
      this.status.textContent = '';
      setTimeout(() => {
        this.status.textContent = message;
      }, 100);
    }

    #rememberPageFocus(link) {
      const page = link.closest('[data-cart-page]');
      const lines = Array.from(page.querySelectorAll('.cart-line'));
      const controls = Array.from(page.querySelectorAll(`[data-key="${CSS.escape(link.dataset.key)}"]`));
      try {
        sessionStorage.setItem(
          PAGE_FOCUS_KEY,
          JSON.stringify({
            key: link.dataset.key,
            index: controls.indexOf(link),
            line: lines.indexOf(link.closest('.cart-line')),
          })
        );
      } catch {}
    }

    #restorePageFocus() {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(PAGE_FOCUS_KEY) || 'null');
        sessionStorage.removeItem(PAGE_FOCUS_KEY);
      } catch {}
      if (!saved) return;

      const page = document.querySelector('[data-cart-page]');
      const controls = page.querySelectorAll(`[data-key="${CSS.escape(saved.key)}"]`);
      let target = controls[saved.index];

      if (!target) {
        const lines = page.querySelectorAll('.cart-line');
        const line = lines[saved.line] || lines[saved.line - 1];
        target = line?.querySelector('.cart-line__name');
      }

      if (!target) {
        target = page.querySelector('#CartPageTitle');
        target?.setAttribute('tabindex', '-1');
      }

      target?.focus();
    }

    #restoreFocus(key, index) {
      const controls = this.#body.querySelectorAll(`[data-key="${CSS.escape(key)}"]`);
      (controls[index] || this.#body).focus({ preventScroll: true });
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
